import React from 'react';
import { JiraIssueCreateErrorBoundary } from '@jira/issue-create__issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import type { JiraIssueCreateErrorBoundaryProps } from '@jira/issue-create__issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/types.tsx';
import { BackgroundPriority } from '@jira/platform__services-non-critical/src/BackgroundPriority.tsx';
import { preloadableAsyncRichTextEditEntryPoint } from '@jira/issue__field-rich-text/src/preloadableAsyncRichTextEditEntryPoint.entrypoint.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const IssueCreateModernisedModalLazy = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-issue-create-modernised-modal" */ './IssueCreateModernisedModal.tsx'
		),
	{ ssr: false },
);

export const preloadIssueCreateModernisedModal = (): void => {
	IssueCreateModernisedModalLazy.preload();
	preloadableAsyncRichTextEditEntryPoint.preload();
};

type AsyncIssueCreateModernisedModalProps = {
	onResetApolloCache?: () => void;
	alwaysShowFullForm?: boolean;
	onError?: JiraIssueCreateErrorBoundaryProps['onError'];
	withBackgroundPriority?: boolean;
};

const IssueCreateModernisedModalWithPriority = ({
	onResetApolloCache,
	alwaysShowFullForm,
	withBackgroundPriority = true,
}: AsyncIssueCreateModernisedModalProps) => {
	const modal = (
		<IssueCreateModernisedModalLazy
			onResetApolloCache={onResetApolloCache}
			alwaysShowFullForm={alwaysShowFullForm}
		/>
	);

	if (!withBackgroundPriority) {
		return modal;
	}

	return <BackgroundPriority>{modal}</BackgroundPriority>;
};

export const AsyncIssueCreateModernisedModal = ({
	onResetApolloCache,
	alwaysShowFullForm,
	onError,
	withBackgroundPriority,
}: AsyncIssueCreateModernisedModalProps): React.JSX.Element => (
	<JiraIssueCreateErrorBoundary
		id="async.jira.issue-create-modernised-modal"
		packageName="jiraIssueCreateModernised"
		sendToPrivacyUnsafeSplunk
		onError={onError}
	>
		<Placeholder name="issue-create-modernised-modal" fallback={<></>}>
			<IssueCreateModernisedModalWithPriority
				onResetApolloCache={onResetApolloCache}
				alwaysShowFullForm={alwaysShowFullForm}
				withBackgroundPriority={withBackgroundPriority}
			/>
		</Placeholder>
	</JiraIssueCreateErrorBoundary>
);
